import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { propertyValue } from '@common/util/properties';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ErrorDialogComponent } from '@portal-core/general/components/error-dialog/error-dialog.component';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { LicenseConnectorsComponent } from '@portal-core/licenses/components/license-connectors/license-connectors/license-connectors.component';
import { LicenseOpenAIFormComponent } from '@portal-core/licenses/components/license-open-ai-form/license-open-ai-form.component';
import { LicenseProfileOptions } from '@portal-core/licenses/components/license-profile-dialog/license-profile-dialog.component';
import { LicenseSecurityFormComponent } from '@portal-core/licenses/components/license-security-form/license-security-form.component';
import { LicenseSettingsFormComponent } from '@portal-core/licenses/components/license-settings-form/license-settings-form.component';
import { LicenseSlackFormComponent } from '@portal-core/licenses/components/license-slack-form/license-slack-form.component';
import { LicenseSsoFormComponent } from '@portal-core/licenses/components/license-sso-form/license-sso-form.component';
import { LicenseProfileTab } from '@portal-core/licenses/enums/license-profile-tab.enum';
import { LicenseType } from '@portal-core/licenses/enums/license-type.enum';
import { LicenseSamlAuthenticationSettings } from '@portal-core/licenses/models/license-saml-authentication-settings.model';
import { License } from '@portal-core/licenses/models/license.model';
import { AuditCategory } from '@portal-core/notifications/models/audit-category.model';
import { NotificationsService } from '@portal-core/notifications/services/notifications.service';
import { CentralPermissions } from '@portal-core/permissions/enums/central-permissions.enum';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { LicenseProfile } from '@portal-core/profiles/models/license-profile.model';
import { ProfileBase } from '@portal-core/profiles/util/profile.base';
import { WebhookChannel } from '@portal-core/slack/models/webhook-channel.model';
import { WebhookSubscriber } from '@portal-core/slack/models/webhook-subscriber.model';
import { SlackWebhookChannelsService } from '@portal-core/slack/services/slack-webhooks-channels.service';
import { SlackWebhooksService } from '@portal-core/slack/services/slack-webhooks.service';
import { McThemePalette } from '@portal-core/ui/themes/types/mc-theme-palette.type';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { catchError, combineLatest, map, Observable, of, switchMap } from 'rxjs';

export enum LicenseProfileForm {
  Overview,
  Subscription,
  Security,
  Settings,
  Integrations,
  Sso
}

@Component({
  selector: 'mc-license-profile',
  templateUrl: './license-profile.component.html',
  styleUrls: ['./license-profile.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LicenseProfileComponent extends ProfileBase<LicenseProfileTab> implements OnInit {

  // color and show tab options
  @Input() color: McThemePalette = 'secondary';
  @Input() showOverview: boolean = true;
  @Input() showSubscription: boolean = true;
  @Input() showSecurity: boolean = true;
  @Input() showSettings: boolean = true;
  @Input() showIntegrations: boolean = true;
  @Input() showSso: boolean = true;

  // main inputs for profile
  @Input() licenseId: number;
  @Input() licenseProfile: LicenseProfile;
  @Input() licenseUser: LicenseUser;
  @Input() licenseProfileOptions: LicenseProfileOptions;
  @Input() expirationDate: Date;
  @Input() userIsAuthor: boolean;
  @Input() subscriptionNotExpired: boolean = true;
  @Input() licenseProfileTabGroupIndex: number;

  @InputObservable('licenseProfile') licenseProfile$: Observable<LicenseProfile>;
  @InputObservable('licenseUser') licenseUser$: Observable<LicenseUser>;
  @InputObservable('expirationDate') expirationDate$: Observable<Date>;
  @InputObservable('userIsAuthor') userIsAuthor$: Observable<boolean>;
  @InputObservable('showSettings') showSettings$: Observable<boolean>;
  @InputObservable('showSubscription') showSubscription$: Observable<boolean>;
  @InputObservable('showIntegrations') showIntegrations$: Observable<boolean>;
  @InputObservable('showSecurity') showSecurity$: Observable<boolean>;
  @InputObservable('showSso') showSso$: Observable<boolean>;

  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() saved: EventEmitter<LicenseProfileForm> = new EventEmitter<LicenseProfileForm>();

  @ViewChild('licenseSsoForm', { static: false }) licenseSsoFormComponent: LicenseSsoFormComponent;
  @ViewChild('licenseSecurityForm', { static: false }) licenseSecurityFormComponent: LicenseSecurityFormComponent;
  @ViewChild('licenseSettingsForm', { static: false }) licenseSettingsFormComponent: LicenseSettingsFormComponent;
  @ViewChild('licenseConnectors', { static: false }) licenseConnectorsComponent: LicenseConnectorsComponent;
  @ViewChild('licenseSlackForm', { static: false }) licenseSlackFormComponent: LicenseSlackFormComponent;
  @ViewChild('licenseOpenAiForm', { static: false }) licenseOpenAIFormComponent: LicenseOpenAIFormComponent;

  auditCategoryTree$: Observable<AuditCategory[]>;
  license$: Observable<License>;
  channels$: Observable<WebhookChannel[]>;
  ssoSettings$: Observable<LicenseSamlAuthenticationSettings>;
  subscribers$: Observable<WebhookSubscriber[]>;
  userCanManageServer$: Observable<boolean>;
  userCanManageIntegrations$: Observable<boolean>;
  showSettingsTab$: Observable<boolean>;
  showSubscriptionTab$: Observable<boolean>;
  showIntegrationsTab$: Observable<boolean>;
  showSecurityTab$: Observable<boolean>;
  showSsoTab$: Observable<boolean>;

  LicenseProfileTab: typeof LicenseProfileTab = LicenseProfileTab;
  LicenseType: typeof LicenseType = LicenseType;

  public get requirePromptOnClose(): boolean {
    return this.dirty;
  }

  get dirty(): boolean {
    switch (this.profileTab) {
      case LicenseProfileTab.SSO: {
        return this.licenseSsoFormComponent.ssoSettingsForm?.dirty;
      }
      case LicenseProfileTab.Security: {
        return this.licenseSecurityFormComponent.changeSecuritySettingsForm.dirty;
      }
      case LicenseProfileTab.Settings: {
        return this.licenseSettingsFormComponent.licenseSettingsForm.dirty;
      }
      case LicenseProfileTab.Integrations: {
        return this.licenseConnectorsComponent?.licenseConnectorsSettingsComponent?.dirty || this.licenseSlackFormComponent?.editSlackNotificationForm?.dirty || this.licenseOpenAIFormComponent?.AIForm.dirty;
      }
    }
  }

  constructor(
    dialog: MatDialog,
    cdr: ChangeDetectorRef,
    private permissionsService: PermissionsService,
    private slackWebhooksService: SlackWebhooksService,
    private slackWebhookChannelsService: SlackWebhookChannelsService,
    private notificationsService: NotificationsService,
    private errorService: ErrorService
  ) {
    super(dialog, cdr);
  }

  ngOnInit() {
    super.ngOnInit();

    // Create observables for the user's permissions
    this.userCanManageServer$ = this.permissionsService.licenseUserHasPermission$(this.licenseUser$, CentralPermissions.ServerManagement);
    this.userCanManageIntegrations$ = this.permissionsService.currentUserHasPermission$(CentralPermissions.ManageIntegrations);

    // Create observable for license
    this.license$ = this.licenseProfile$.pipe(map(licenseProfile => (licenseProfile?.License)))

    this.ssoSettings$ = this.licenseProfile$.pipe(
      map(licenseProfile => licenseProfile?.LicenseSamlAuthenticationSettings)
    );

    // Create an observable for the slack subscribers BUT only for authors
    this.subscribers$ = this.userIsAuthor$.pipe(
      switchMap(userIsAuthor => {
        if (userIsAuthor) {
          return this.slackWebhooksService.getSubscribers$(this.licenseId, { forceApiRequest: true });
        } else {
          return of(null);
        }
      })
    );

    // Create an observable for the slack channels BUT only for authors
    this.channels$ = this.userIsAuthor$.pipe(
      switchMap(userIsAuthor => {
        if (userIsAuthor) {
          return this.slackWebhookChannelsService.getChannels$(this.licenseId, { forceApiRequest: true });
        } else {
          return of(null);
        }
      })
    );

    // Create an observable for notifications default categories
    this.auditCategoryTree$ = this.notificationsService.getAuditCategoryTree$().pipe(
      catchError((errorResponse: HttpErrorResponse) => this.handleError$(errorResponse))
    );

    // Create observables for which tabs to show in the sidebar
    this.showSettingsTab$ = combineLatest([this.showSettings$, this.userCanManageServer$]).pipe(
      map(([showSettings, userCanManageServer]) => showSettings && (userCanManageServer || propertyValue(this.licenseProfileOptions, 'showSettingsTab')))
    );
    this.showSubscriptionTab$ = this.showSubscription$;
    this.showIntegrationsTab$ = combineLatest([this.showIntegrations$, this.userCanManageIntegrations$]).pipe(
      map(([showIntegrations, userCanManageIntegrations]) => showIntegrations && (userCanManageIntegrations || propertyValue(this.licenseProfileOptions, 'showIntegrationsTab')))
    );
    this.showSecurityTab$ = combineLatest([this.showSecurity$, this.userCanManageServer$]).pipe(
      map(([showSecurity, userCanManageServer]) => showSecurity && (userCanManageServer || propertyValue(this.licenseProfileOptions, 'showSecurityTab')))
    );
    this.showSsoTab$ = combineLatest([this.showSso$, this.userCanManageServer$]).pipe(
      map(([showSso, userCanManageServer]) => showSso && (userCanManageServer || propertyValue(this.licenseProfileOptions, 'showSsoTab')))
    );
  }

  private handleError$(errorResponse: HttpErrorResponse): Observable<any> {
    this.dialog.open(ErrorDialogComponent, {
      ...ErrorDialogComponent.DialogConfig,
      data: {
        title: 'Unexpected error',
        message: 'An error occurred either from pulling subscriptions from the user service or retrieving audit category trees',
        errors: this.errorService.getErrorMessages(errorResponse)
      }
    });
    return of(null);
  }

  onCancel() {
    this.cancel.emit();
  }

  onSettingsSaved() {
    this.saved.emit(LicenseProfileForm.Settings);
  }

  onSecuritySaved() {
    this.saved.emit(LicenseProfileForm.Security);
  }

  onSsoSaved() {
    this.saved.emit(LicenseProfileForm.Sso);
  }
}
